import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, UploadCloud, X, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { CATEGORY_LABELS, resolveMediaUrl } from "@/pages/ImpactHub";

const PROGRAMMES = [
    "livelihoods", "vulnerable_families", "youth", "women", "disability",
    "golden_years", "health", "education", "infrastructure", "warren_park_connect",
];

const EMPTY = {
    slug: "", title: "", category: "", programme: "", summary: "",
    background: "", challenge: "", intervention: "", outcome: "",
    location_name: "", location_lat: null, location_lng: null,
    start_date: "", completion_date: "", status: "completed",
    verified: false, featured: false, is_success_story: false,
    beneficiaries_count: 0, funding_target: 0, funding_current: 0, currency: "USD",
    funding_partners: [], images: [], videos: [], documents: [], testimonials: [], tags: [],
    published: false,
};

export default function AdminProjects() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        try { const { data } = await api.get("/admin/projects"); setItems(data.items || []); }
        catch (e) { toast.error(formatApiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    async function save(draft) {
        try { await api.post("/admin/projects", draft); toast.success("Saved"); setEditing(null); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    }
    async function del(slug) {
        if (!confirm(`Delete project "${slug}"?`)) return;
        try { await api.delete(`/admin/projects/${slug}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <div data-testid="admin-projects">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="gsf-eyebrow">Impact Hub</div>
                    <h1 className="mt-2 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">Projects</h1>
                </div>
                <button onClick={() => setEditing({ ...EMPTY })} className="gsf-btn-primary text-sm" data-testid="new-project-btn"><Plus size={16} /> New project</button>
            </div>

            <div className="mt-6 gsf-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-[#f8faf7] text-left">
                        <tr><th className="p-3">Title</th><th className="p-3">Category</th><th className="p-3">Status</th><th className="p-3">Verified</th><th className="p-3">Published</th><th className="p-3">Actions</th></tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr><td colSpan={6} className="p-6 text-gray-500 text-center">No projects yet. Click "New project" to add one.</td></tr>
                        ) : items.map((p) => (
                            <tr key={p.id} className="border-t border-gray-100" data-testid={`project-row-${p.slug}`}>
                                <td className="p-3 font-medium">{p.title}<div className="text-xs text-gray-500">{p.slug}</div></td>
                                <td className="p-3 text-xs">{CATEGORY_LABELS[p.category] || p.category || "—"}</td>
                                <td className="p-3 text-xs capitalize">{p.status || "—"}</td>
                                <td className="p-3">{p.verified ? <CheckCircle2 size={16} className="text-green-600" /> : "—"}</td>
                                <td className="p-3 text-xs">{p.published ? "Yes" : "Draft"}</td>
                                <td className="p-3 flex gap-2">
                                    <button onClick={() => setEditing({ ...EMPTY, ...p })} className="text-[color:var(--gsf-green-700)] hover:bg-[color:var(--gsf-soft-green)] rounded-lg p-1.5 text-xs" data-testid={`edit-${p.slug}`}>Edit</button>
                                    <button onClick={() => del(p.slug)} className="text-red-600 hover:bg-red-50 rounded-lg p-1.5" data-testid={`del-${p.slug}`}><Trash2 size={14} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && <ProjectEditor draft={editing} onCancel={() => setEditing(null)} onSave={save} />}
        </div>
    );
}

function ProjectEditor({ draft, onCancel, onSave }) {
    const [d, setD] = useState(draft);
    const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    async function upload(files, target) {
        if (!files?.length) return;
        setUploading(true);
        try {
            for (const f of Array.from(files)) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                if (target === "images") set("images", [...(d.images || []), data.url]);
                if (target === "documents") set("documents", [...(d.documents || []), { name: f.name, url: data.url, type: f.type }]);
            }
            toast.success("Upload complete");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setUploading(false); }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/60 overflow-y-auto py-10 px-4" data-testid="project-editor">
            <div className="max-w-4xl mx-auto bg-white rounded-2xl p-8 shadow-2xl">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <div className="gsf-eyebrow">{d.slug ? "Edit project" : "New project"}</div>
                        <h2 className="mt-1 font-serif text-2xl text-[color:var(--gsf-green-700)] font-bold">Project details</h2>
                    </div>
                    <button onClick={onCancel} className="text-gray-400 hover:text-gray-600" data-testid="editor-close"><X size={20} /></button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Text label="Title" value={d.title} onChange={(v) => set("title", v)} testid="p-title" required />
                    <Text label="Slug (leave blank for auto)" value={d.slug} onChange={(v) => set("slug", v)} testid="p-slug" />

                    <Select label="Category" value={d.category} onChange={(v) => set("category", v)} testid="p-category"
                        options={[["", "—"], ...Object.entries(CATEGORY_LABELS)]} />
                    <Select label="Programme" value={d.programme} onChange={(v) => set("programme", v)} testid="p-programme"
                        options={[["", "—"], ...PROGRAMMES.map((p) => [p, p.replace(/_/g, " ")])]} />

                    <Text label="Location" value={d.location_name} onChange={(v) => set("location_name", v)} testid="p-loc" />
                    <div className="grid grid-cols-2 gap-2">
                        <Text label="Latitude" value={d.location_lat ?? ""} onChange={(v) => set("location_lat", v ? parseFloat(v) : null)} testid="p-lat" />
                        <Text label="Longitude" value={d.location_lng ?? ""} onChange={(v) => set("location_lng", v ? parseFloat(v) : null)} testid="p-lng" />
                    </div>

                    <Text label="Start date (YYYY-MM-DD)" value={d.start_date || ""} onChange={(v) => set("start_date", v)} testid="p-start" />
                    <Text label="Completion date (YYYY-MM-DD)" value={d.completion_date || ""} onChange={(v) => set("completion_date", v)} testid="p-completion" />

                    <Select label="Status" value={d.status} onChange={(v) => set("status", v)} testid="p-status" options={[["planned", "planned"], ["active", "active"], ["completed", "completed"]]} />
                    <div className="grid grid-cols-3 gap-2">
                        <Num label="Beneficiaries" value={d.beneficiaries_count} onChange={(v) => set("beneficiaries_count", v)} testid="p-benef" />
                        <Num label="Funding target" value={d.funding_target} onChange={(v) => set("funding_target", v)} testid="p-target" />
                        <Num label="Funding current" value={d.funding_current} onChange={(v) => set("funding_current", v)} testid="p-current" />
                    </div>
                </div>

                <Text label="Summary (short)" value={d.summary} onChange={(v) => set("summary", v)} testid="p-summary" className="mt-4" />
                <Area label="Background" value={d.background} onChange={(v) => set("background", v)} testid="p-background" />
                <Area label="Challenge" value={d.challenge} onChange={(v) => set("challenge", v)} testid="p-challenge" />
                <Area label="Intervention" value={d.intervention} onChange={(v) => set("intervention", v)} testid="p-intervention" />
                <Area label="Outcome" value={d.outcome} onChange={(v) => set("outcome", v)} testid="p-outcome" />

                <div className="mt-6">
                    <div className="flex items-center justify-between">
                        <div className="gsf-eyebrow">Photos</div>
                        <button onClick={() => fileRef.current?.click()} className="gsf-btn-outline text-xs" data-testid="upload-image-btn"><UploadCloud size={14} /> {uploading ? "Uploading…" : "Upload images"}</button>
                        <input ref={fileRef} type="file" multiple accept="image/*" hidden onChange={(e) => upload(e.target.files, "images")} data-testid="upload-image-input" />
                    </div>
                    <div className="mt-3 grid grid-cols-3 md:grid-cols-6 gap-2">
                        {(d.images || []).map((u, i) => (
                            <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200" data-testid={`img-${i}`}>
                                <img src={resolveMediaUrl(u)} alt="" className="w-full h-full object-cover" />
                                <button onClick={() => set("images", d.images.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100"><X size={12} /></button>
                            </div>
                        ))}
                    </div>
                </div>

                <VideosEditor videos={d.videos || []} onChange={(v) => set("videos", v)} />
                <DocsEditor documents={d.documents || []} onChange={(v) => set("documents", v)} />
                <TestimonialsEditor testimonials={d.testimonials || []} onChange={(v) => set("testimonials", v)} />

                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Check label="Verified" checked={d.verified} onChange={(v) => set("verified", v)} testid="p-verified" />
                    <Check label="Featured" checked={d.featured} onChange={(v) => set("featured", v)} testid="p-featured" />
                    <Check label="Success story" checked={d.is_success_story} onChange={(v) => set("is_success_story", v)} testid="p-success" />
                    <Check label="Published" checked={d.published} onChange={(v) => set("published", v)} testid="p-published" />
                </div>

                <div className="mt-8 flex justify-end gap-3">
                    <button onClick={onCancel} className="gsf-btn-outline text-sm" data-testid="editor-cancel">Cancel</button>
                    <button onClick={() => onSave(d)} className="gsf-btn-primary text-sm" data-testid="editor-save"><Save size={14} /> Save project</button>
                </div>
            </div>
        </div>
    );
}

function VideosEditor({ videos, onChange }) {
    const add = () => onChange([...videos, { title: "", url: "" }]);
    return (
        <div className="mt-6">
            <div className="flex items-center justify-between">
                <div className="gsf-eyebrow">Videos (YouTube / Vimeo)</div>
                <button onClick={add} className="gsf-btn-outline text-xs" data-testid="add-video"><Plus size={14} /> Add</button>
            </div>
            {videos.map((v, i) => (
                <div key={i} className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid={`vid-row-${i}`}>
                    <input placeholder="Title" value={v.title || ""} onChange={(e) => onChange(videos.map((r, j) => j === i ? { ...r, title: e.target.value } : r))} className="rounded border border-gray-200 px-2 py-1.5 text-sm" />
                    <div className="flex gap-2">
                        <input placeholder="https://youtube.com/... or vimeo.com/..." value={v.url || ""} onChange={(e) => onChange(videos.map((r, j) => j === i ? { ...r, url: e.target.value } : r))} className="flex-1 rounded border border-gray-200 px-2 py-1.5 text-sm" />
                        <button onClick={() => onChange(videos.filter((_, j) => j !== i))} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 size={14} /></button>
                    </div>
                </div>
            ))}
        </div>
    );
}

function DocsEditor({ documents, onChange }) {
    const fileRef = useRef(null);
    async function up(files) {
        if (!files?.length) return;
        try {
            for (const f of Array.from(files)) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                onChange([...documents, { name: f.name, url: data.url, type: f.type }]);
            }
            toast.success("Document uploaded");
        } catch (e) { toast.error(formatApiError(e)); }
    }
    return (
        <div className="mt-6">
            <div className="flex items-center justify-between">
                <div className="gsf-eyebrow">Documents / reports</div>
                <button onClick={() => fileRef.current?.click()} className="gsf-btn-outline text-xs" data-testid="upload-doc-btn"><UploadCloud size={14} /> Upload PDFs</button>
                <input ref={fileRef} type="file" hidden multiple accept=".pdf,image/*" onChange={(e) => up(e.target.files)} />
            </div>
            {documents.map((doc, i) => (
                <div key={i} className="mt-2 flex items-center gap-2 text-sm" data-testid={`doc-row-${i}`}>
                    <span className="flex-1 truncate">{doc.name} <span className="text-gray-400 text-xs">{doc.url}</span></span>
                    <button onClick={() => onChange(documents.filter((_, j) => j !== i))} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 size={14} /></button>
                </div>
            ))}
        </div>
    );
}

function TestimonialsEditor({ testimonials, onChange }) {
    const add = () => onChange([...testimonials, { name: "", role: "", quote: "" }]);
    return (
        <div className="mt-6">
            <div className="flex items-center justify-between">
                <div className="gsf-eyebrow">Testimonials</div>
                <button onClick={add} className="gsf-btn-outline text-xs" data-testid="add-test"><Plus size={14} /> Add</button>
            </div>
            {testimonials.map((t, i) => (
                <div key={i} className="mt-2 gsf-card p-3 grid grid-cols-1 md:grid-cols-4 gap-2" data-testid={`test-row-${i}`}>
                    <input placeholder="Name" value={t.name || ""} onChange={(e) => onChange(testimonials.map((r, j) => j === i ? { ...r, name: e.target.value } : r))} className="rounded border border-gray-200 px-2 py-1.5 text-sm" />
                    <input placeholder="Role (optional)" value={t.role || ""} onChange={(e) => onChange(testimonials.map((r, j) => j === i ? { ...r, role: e.target.value } : r))} className="rounded border border-gray-200 px-2 py-1.5 text-sm" />
                    <input placeholder="Quote" value={t.quote || ""} onChange={(e) => onChange(testimonials.map((r, j) => j === i ? { ...r, quote: e.target.value } : r))} className="md:col-span-2 rounded border border-gray-200 px-2 py-1.5 text-sm" />
                    <button onClick={() => onChange(testimonials.filter((_, j) => j !== i))} className="text-red-600 text-xs">Remove</button>
                </div>
            ))}
        </div>
    );
}

function Text({ label, value, onChange, testid, required, className }) {
    return (
        <label className={`block ${className || ""}`}>
            <span className="text-xs text-gray-500">{label}{required && <span className="text-red-500"> *</span>}</span>
            <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} required={required} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
        </label>
    );
}
function Num({ label, value, onChange, testid }) {
    return (
        <label className="block">
            <span className="text-xs text-gray-500">{label}</span>
            <input type="number" value={value ?? 0} onChange={(e) => onChange(parseInt(e.target.value) || 0)} data-testid={testid} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
        </label>
    );
}
function Area({ label, value, onChange, testid }) {
    return (
        <label className="block mt-4">
            <span className="text-xs text-gray-500">{label}</span>
            <textarea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
        </label>
    );
}
function Select({ label, value, onChange, testid, options }) {
    return (
        <label className="block">
            <span className="text-xs text-gray-500">{label}</span>
            <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
                {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
        </label>
    );
}
function Check({ label, checked, onChange, testid }) {
    return (
        <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
            {label}
        </label>
    );
}
